import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ImagePlus, Loader2, ScanLine, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, mediaUrl } from "@/lib/api";
import { LocationPicker } from "@/components/LocationPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const MAX_GALLERY = 7;

const EMPTY = {  business_name: "",
  phone_number: "",
  state: "",
  district: "",
  category: "",
  street_address: "",
  postcode: "",
  description: "",
  owner_email: "",
  status: "approved",
};

const fromListing = (listing) => ({
  business_name: listing.business_name || "",
  phone_number: listing.phone_number || "",
  state: listing.state || "",
  district: listing.district || "",
  category: listing.category || "",
  street_address: listing.street_address || "",
  postcode: listing.postcode || "",
  description: listing.description || "",
  owner_email: listing.owner_email || "",
  status: listing.status || "approved",
});

export const AdminListingDialog = ({ locations, listing, open, onOpenChange, onSaved }) => {
  const fileRef = useRef(null);
  const photoRef = useRef(null);
  const [form, setForm] = useState(EMPTY);
  const [storagePath, setStoragePath] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [pin, setPin] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const editing = Boolean(listing);

  useEffect(() => {
    if (!open) return;
    setForm(listing ? fromListing(listing) : EMPTY);
    setStoragePath(null);
    setPin(listing?.lat && listing?.lng ? { lat: listing.lat, lng: listing.lng } : null);
    setPhotos(
      (listing?.photos || []).map((url, i) => ({
        preview: mediaUrl(url),
        storage_path: (listing?.photo_paths || [])[i] || url.replace("/api/files/", ""),
      }))
    );
  }, [open, listing]);

  const set = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));
  const districts = form.state ? locations.states?.[form.state] || [] : [];

  const scanCard = async (event) => {
    const chosen = event.target.files?.[0];
    if (!chosen) return;
    setScanning(true);
    const body = new FormData();
    body.append("file", chosen);
    try {
      const { data } = await api.post("/listings/upload-card", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setStoragePath(data.storage_path);
      setForm((prev) => ({
        ...prev,
        business_name: data.business_name || prev.business_name,
        phone_number: data.phone_number || prev.phone_number,
        street_address: data.street_address || prev.street_address,
        postcode: data.postcode || prev.postcode,
        state: data.state && locations.states?.[data.state] ? data.state : prev.state,
        district: data.district || prev.district,
      }));
      toast.success("Kad dibaca — sahkan maklumat di bawah");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setScanning(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const uploadExtraPhoto = async (event) => {
    const chosen = Array.from(event.target.files || []);
    if (!chosen.length) return;
    if (chosen.some((f) => f.size > 8 * 1024 * 1024)) {
      toast.error("Setiap gambar mesti kurang daripada 8MB");
      if (photoRef.current) photoRef.current.value = "";
      return;
    }
    const room = MAX_GALLERY - photos.length;
    if (room <= 0) {
      toast.error(`Maksimum ${MAX_GALLERY} gambar`);
      return;
    }
    setPhotoUploading(true);
    try {
      const uploaded = [];
      for (const file of chosen.slice(0, room)) {
        const body = new FormData();
        body.append("file", file);
        const { data } = await api.post("/listings/upload-photo", body, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push({ preview: mediaUrl(data.image_url), storage_path: data.storage_path });
      }
      setPhotos((prev) => [...prev, ...uploaded]);
      toast.success(`${uploaded.length} gambar dimuat naik`);
      if (chosen.length > room) toast.info(`Hanya ${room} gambar diterima (had ${MAX_GALLERY})`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setPhotoUploading(false);
      if (photoRef.current) photoRef.current.value = "";
    }
  };

  const movePhoto = (index, direction) =>
    setPhotos((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      owner_email: form.owner_email || null,
      photo_paths: photos.map((p) => p.storage_path).filter(Boolean),
      lat: pin?.lat ?? null,
      lng: pin?.lng ?? null,
    };
    try {
      const { data } = editing
        ? await api.patch(`/admin/listings/${listing.id}`, payload)
        : await api.post("/admin/listings", { ...payload, storage_path: storagePath });
      toast.success(editing ? `"${data.business_name}" dikemas kini` : `"${data.business_name}" ditambah`);
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="admin-listing-dialog"
        className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"
      >
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            {editing ? `Sunting: ${listing.business_name}` : "Tambah senarai baru"}
          </DialogTitle>
          <DialogDescription>
            {editing
              ? "Perubahan terus terpakai pada senarai awam."
              : "Senarai admin terus LIVE tanpa paywall. Muat naik kad perniagaan untuk auto-isi."}
          </DialogDescription>
        </DialogHeader>

        {!editing && (
          <>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={scanCard}
              className="hidden"
              data-testid="admin-add-listing-card-input"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={scanning}
              data-testid="admin-add-listing-scan-button"
              onClick={() => fileRef.current?.click()}
              className="w-fit"
            >
              {scanning ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <ScanLine className="mr-1.5 h-4 w-4" />
              )}
              {scanning ? "AI membaca kad…" : storagePath ? "Tukar kad perniagaan" : "Auto-isi dari kad"}
            </Button>
          </>
        )}

        <form onSubmit={submit} className="mt-2 grid gap-5 sm:grid-cols-2">
          <div>
            <Label htmlFor="admin-name">Nama bisnes *</Label>
            <Input
              id="admin-name"
              data-testid="admin-add-name-input"
              value={form.business_name}
              onChange={(e) => set("business_name")(e.target.value)}
              className="mt-2 h-11"
              required
            />
          </div>
          <div>
            <Label htmlFor="admin-phone">Nombor telefon *</Label>
            <Input
              id="admin-phone"
              data-testid="admin-add-phone-input"
              value={form.phone_number}
              onChange={(e) => set("phone_number")(e.target.value)}
              placeholder="012-345 6789"
              className="mt-2 h-11"
              required
            />
          </div>
          <div>
            <Label>Kategori *</Label>
            <Select value={form.category} onValueChange={set("category")}>
              <SelectTrigger data-testid="admin-add-category-select" className="mt-2 h-11">
                <SelectValue placeholder="Pilih kategori" />
              </SelectTrigger>
              <SelectContent>
                {(locations.categories || []).map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={set("status")}>
              <SelectTrigger data-testid="admin-add-status-select" className="mt-2 h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="approved">LIVE (approved)</SelectItem>
                <SelectItem value="pending">Pending semakan</SelectItem>
                {editing && <SelectItem value="rejected">Rejected</SelectItem>}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Negeri *</Label>
            <Select
              value={form.state}
              onValueChange={(value) => setForm((prev) => ({ ...prev, state: value, district: "" }))}
            >
              <SelectTrigger data-testid="admin-add-state-select" className="mt-2 h-11">
                <SelectValue placeholder="Pilih negeri" />
              </SelectTrigger>
              <SelectContent>
                {Object.keys(locations.states || {}).map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Daerah *</Label>
            <Select value={form.district} onValueChange={set("district")} disabled={!form.state}>
              <SelectTrigger data-testid="admin-add-district-select" className="mt-2 h-11">
                <SelectValue placeholder={form.state ? "Pilih daerah" : "Pilih negeri dahulu"} />
              </SelectTrigger>
              <SelectContent>
                {districts.map((d) => (
                  <SelectItem key={d} value={d}>
                    {d}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="admin-street">Alamat jalan</Label>
            <Input
              id="admin-street"
              data-testid="admin-add-street-input"
              value={form.street_address}
              onChange={(e) => set("street_address")(e.target.value)}
              className="mt-2 h-11"
            />
          </div>
          <div>
            <Label htmlFor="admin-postcode">Poskod</Label>
            <Input
              id="admin-postcode"
              data-testid="admin-add-postcode-input"
              value={form.postcode}
              onChange={(e) => set("postcode")(e.target.value)}
              placeholder="43300"
              className="mt-2 h-11"
            />
          </div>
          <div>
            <Label htmlFor="admin-email">Emel pemilik (pilihan)</Label>
            <Input
              id="admin-email"
              type="email"
              data-testid="admin-add-email-input"
              value={form.owner_email}
              onChange={(e) => set("owner_email")(e.target.value)}
              className="mt-2 h-11"
            />
          </div>
          <div className="sm:col-span-2" data-testid="admin-location-field">
            <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Lokasi pada peta</Label>
            <LocationPicker
              address={form.street_address}
              postcode={form.postcode}
              district={form.district}
              state={form.state}
              value={pin}
              onChange={setPin}
            />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="admin-desc">Keterangan</Label>
            <Textarea
              id="admin-desc"
              data-testid="admin-add-description-input"
              value={form.description}
              onChange={(e) => set("description")(e.target.value)}
              rows={3}
              className="mt-2"
            />
          </div>
          <div className="sm:col-span-2" data-testid="admin-extra-photo-field">
            <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">
              Gambar galeri (pilihan — sehingga {MAX_GALLERY})
            </Label>
            <p className="mt-1.5 text-xs text-slate-500">
              Boleh pilih beberapa gambar sekali gus. Gambar pertama dipaparkan di bawah kad perniagaan;
              yang lain masuk galeri (tidak ditunjukkan dalam direktori).
            </p>
            {photos.length > 0 && (
              <div className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-6">
                {photos.map((p, i) => (
                  <div key={p.storage_path || i} className="relative overflow-hidden rounded-md border border-border">
                    <img src={p.preview} alt="" className="aspect-square w-full object-cover" />
                    <button
                      type="button"
                      data-testid={`admin-photo-remove-${i}`}
                      onClick={() => setPhotos((prev) => prev.filter((_, idx) => idx !== i))}
                      className="absolute right-1 top-1 rounded bg-red-600/90 p-1 text-white transition-colors duration-200 hover:bg-red-700"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                    {i === 0 && (
                      <span className="absolute left-0 top-1 bg-slate-900/80 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                        Utama
                      </span>
                    )}
                    <div className="absolute inset-x-0 bottom-0 flex justify-between bg-slate-900/60 p-0.5">
                      <button
                        type="button"
                        data-testid={`admin-photo-left-${i}`}
                        disabled={i === 0}
                        onClick={() => movePhoto(i, -1)}
                        className="rounded bg-white/90 p-0.5 text-slate-900 transition-colors duration-200 hover:bg-white disabled:opacity-30"
                        aria-label="Alih ke kiri"
                      >
                        <ChevronLeft className="h-3 w-3" />
                      </button>
                      <button
                        type="button"
                        data-testid={`admin-photo-right-${i}`}
                        disabled={i === photos.length - 1}
                        onClick={() => movePhoto(i, 1)}
                        className="rounded bg-white/90 p-0.5 text-slate-900 transition-colors duration-200 hover:bg-white disabled:opacity-30"
                        aria-label="Alih ke kanan"
                      >
                        <ChevronRight className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <input
              ref={photoRef}
              type="file"
              multiple
              accept="image/png,image/jpeg,image/webp"
              onChange={uploadExtraPhoto}
              className="hidden"
              data-testid="admin-extra-photo-input"
            />
            <Button
              type="button"
              variant="outline"
              disabled={photoUploading || photos.length >= MAX_GALLERY}
              data-testid="admin-extra-photo-button"
              onClick={() => photoRef.current?.click()}
              className="mt-3"
            >
              {photoUploading ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <ImagePlus className="mr-1.5 h-4 w-4" />
              )}
              {photoUploading
                ? "Memuat naik…"
                : photos.length
                  ? `Tambah gambar (${photos.length}/${MAX_GALLERY})`
                  : "Pilih gambar galeri"}
            </Button>
          </div>

          <div className="sm:col-span-2">
            <Button
              type="submit"
              data-testid="admin-add-listing-submit"
              disabled={
                saving ||
                !form.business_name ||
                !form.phone_number ||
                !form.category ||
                !form.state ||
                !form.district
              }
              className="h-11 w-full sm:w-fit sm:px-8"
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editing ? "Simpan perubahan" : "Simpan senarai"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
